import { Logger } from '../utils/logger';

export type RequestPriority = 'high' | 'normal' | 'low';

export interface QueuedRequest {
  id: string;
  priority: RequestPriority;
  frame: Uint8Array;
  send: (frame: Uint8Array) => Promise<Uint8Array>;
  timeout: number;
  retriesRemaining: number;
  retryDelay: number;
  resolve: (value: Uint8Array) => void;
  reject: (reason: Error) => void;
  timerId?: ReturnType<typeof setTimeout>;
  retryTimer?: ReturnType<typeof setTimeout>;
  timestamp: number;
}

export interface RequestQueueOptions {
  maxConcurrent: number;
  defaultTimeout: number;
  defaultRetryCount: number;
  defaultRetryDelay: number;
}

const DEFAULT_OPTIONS: RequestQueueOptions = {
  maxConcurrent: 1,
  defaultTimeout: 2000,
  defaultRetryCount: 3,
  defaultRetryDelay: 500,
};

export class RequestQueue {
  private queue: QueuedRequest[] = [];
  private activeRequest: QueuedRequest | null = null;
  private options: RequestQueueOptions;
  private nextId = 0;

  constructor(
    options?: Partial<RequestQueueOptions>,
    private logger?: Logger,
  ) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Enfileira uma transação Modbus.
   *
   * @param send - função de transação que envia o frame e resolve com a
   *               resposta do escravo (ex.: SerialManager.transact).
   */
  enqueue(
    frame: Uint8Array,
    send: (frame: Uint8Array) => Promise<Uint8Array>,
    priority: RequestPriority = 'normal',
    timeout?: number,
    retryCount?: number,
  ): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      const id = `req-${++this.nextId}`;

      const request: QueuedRequest = {
        id,
        priority,
        frame,
        send,
        timeout: timeout ?? this.options.defaultTimeout,
        retriesRemaining: retryCount ?? this.options.defaultRetryCount,
        retryDelay: this.options.defaultRetryDelay,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.queue.push(request);
      this.logger?.log(`[QUEUE] Enfileirado ${id} (prio: ${priority})`, 'system');
      this.processNext();
    });
  }

  clear(): void {
    for (const req of this.queue) {
      req.reject(new Error('Request queue cleared'));
    }
    this.queue = [];

    if (this.activeRequest) {
      if (this.activeRequest.timerId) {
        clearTimeout(this.activeRequest.timerId);
      }
      if (this.activeRequest.retryTimer) {
        clearTimeout(this.activeRequest.retryTimer);
      }
      this.activeRequest.reject(new Error('Request queue cleared'));
      this.activeRequest = null;
    }

    this.logger?.log('[QUEUE] Fila limpa', 'system');
  }

  pendingCount(): number {
    return this.queue.length;
  }

  private processNext(): void {
    if (this.activeRequest !== null) return;

    const request = this.dequeueHighestPriority();
    if (!request) return;

    this.activeRequest = request;
    void this.executeRequest(request);
  }

  private dequeueHighestPriority(): QueuedRequest | undefined {
    const priorityOrder: Record<RequestPriority, number> = {
      high: 0,
      normal: 1,
      low: 2,
    };

    let bestIndex = -1;
    let bestScore = Infinity;

    for (let i = 0; i < this.queue.length; i++) {
      const req = this.queue[i];
      const score = priorityOrder[req.priority];

      if (score < bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }

    if (bestIndex === -1) return undefined;
    return this.queue.splice(bestIndex, 1)[0];
  }

  private async executeRequest(request: QueuedRequest): Promise<void> {
    const startTime = Date.now();

    try {
      const response = await this.sendWithTimeout(request);

      this.logger?.log(
        `[QUEUE] ${request.id} concluído em ${Date.now() - startTime}ms`,
        'system',
      );

      this.activeRequest = null;
      request.resolve(response);
      this.processNext();
    } catch (err) {
      if (request.retriesRemaining > 0) {
        request.retriesRemaining--;
        this.logger?.log(
          `[QUEUE] ${request.id} falhou, retentativas restantes: ${request.retriesRemaining}`,
          'warn',
        );

        this.activeRequest = null;
        request.retryTimer = setTimeout(() => {
          request.retryTimer = undefined;
          this.queue.push(request);
          this.processNext();
        }, request.retryDelay);

        return;
      }

      this.logger?.log(
        `[QUEUE] ${request.id} falhou sem retentativas restantes`,
        'error',
      );

      this.activeRequest = null;
      request.reject(
        err instanceof Error ? err : new Error(String(err)),
      );
      this.processNext();
    }
  }

  private sendWithTimeout(request: QueuedRequest): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      const timer = setTimeout(() => {
        request.timerId = undefined;
        reject(new Error(`Timeout after ${request.timeout}ms`));
      }, request.timeout);

      request.timerId = timer;

      request
        .send(request.frame)
        .then((resp) => {
          clearTimeout(timer);
          request.timerId = undefined;
          resolve(resp);
        })
        .catch((err) => {
          clearTimeout(timer);
          request.timerId = undefined;
          reject(err);
        });
    });
  }
}
